<template>
  <div class="fts-flow-diagram">
    <div :id="diagramId" class="flow-diagram"></div>
    <div class="flow-diagram-fab" v-if="expandable">
      <fts-btn text icon @click="expand">
        <font-awesome-icon :icon="['fas', 'expand-alt']" />
      </fts-btn>
    </div>
    <!-- flow diagram modal: only an expandable diagram opens one. The modal's own diagram must
         not nest another — the two components import each other, and resolved from the modal's
         side this tag is still undefined ("Unknown custom element"). -->
    <fts-flow-diagram-modal ref="ftsFlowDiagramModal" v-if="expandable" />
  </div>
</template>

<script>
import cytoscape from "cytoscape";
import dagre from "cytoscape-dagre";
import flowDiagram from "../library/flowDiagram";

import FTSBtn from "./FTSBtn";
import FTSFlowDiagramModal from "./FTSFlowDiagramModal";

cytoscape.use(dagre);

const FIT_PADDING = 40;
const ZOOM_RANGE = [0.4, 2];

export default {
  components: {
    "fts-btn": FTSBtn,
    "fts-flow-diagram-modal": FTSFlowDiagramModal
  },
  props: {
    expandable: Boolean,
    diagramData: Object,
    // A tap on an activity pins its highlight until the next tap / clearHighlight(). Only a
    // parent that shows something for it (the modal's form card) should turn this on.
    pinnable: Boolean
  },
  data: () => ({
    diagramId: null
  }),
  methods: {
    // bundled labels are placed by distance, which only exists once the layout has run
    afterLayout() {
      this.cy.style().update();
      this.fit();
    },
    expand() {
      this.$refs.ftsFlowDiagramModal.show(this.diagramData);
    },
    // Fit, but never above 1:1 — a five-state workflow is not blown up to fill the modal.
    fit() {
      if (!this.cy) return;
      this.cy.fit(undefined, FIT_PADDING);
      if (this.cy.zoom() > 1) {
        this.cy.zoom(1);
        this.cy.center();
      }
    },
    zoomBy(factor) {
      if (!this.cy) return;
      const level = Math.max(ZOOM_RANGE[0], Math.min(ZOOM_RANGE[1], this.cy.zoom() * factor));
      this.cy.zoom({
        level,
        renderedPosition: { x: this.cy.width() / 2, y: this.cy.height() / 2 }
      });
    },
    redrawDiagram() {
      let vm = this;
      let diagramData = this.diagramData || {};
      if (vm.cy) vm.cy.destroy();
      vm.pinned = null;

      const elements = flowDiagram.getElements(
        diagramData.activities || [],
        diagramData.states || [],
        diagramData.flows || [],
        [],
        diagramData.diagram,
        { hideOrphans: true }
      );
      vm.edges = elements.edges;

      let cy = vm.cy = cytoscape({
        container: document.getElementById(vm.diagramId),
        wheelSensitivity: 0.1,
        autounselectify: true,
        layout: diagramData.diagram != null ? { name: "preset" } : flowDiagram.getLayout(elements),
        style: flowDiagram.getStyle(),
        elements
      });
      // Hover lights a flow and tells the parent what was lit and where its chip sits, so the
      // parent can hang a card on it. Additive — usages that don't listen still get the
      // highlight, and nothing else.
      cy.on("mouseover", "node, edge", function(e) {
        if (vm.pinned) return;
        vm.light(vm.hotFor(e.target), e.target, e.originalEvent);
      });
      cy.on("mouseout", "node, edge", function() {
        if (vm.pinned) return;
        vm.light(null);
      });
      cy.on("tap", function(e) {
        vm.tap(e.target, e.originalEvent);
      });
      // a pinned card follows its chip while the view moves
      cy.on("pan zoom resize", function() {
        if (vm.pinned) vm.report(vm.pinned.hot, vm.pinned.target, null, true);
      });
      vm.afterLayout();
    },
    /* What a hovered element stands for — see flowDiagram.getHighlight. */
    hotFor(target) {
      if (target.isNode()) return { kind: "state", id: target.id() };
      const activityId = target.data("activityId");
      return activityId != null ? { kind: "activity", id: activityId } : { kind: "edge", id: target.id() };
    },
    /* Light `hot` (null clears) and report it to the parent. */
    light(hot, target, originalEvent) {
      this.paint(hot);
      this.report(hot, target, originalEvent, false);
    },
    paint(hot, pinnedTarget) {
      if (!this.cy) return;
      const lit = flowDiagram.getHighlight(this.edges || [], hot);
      const edges = new Set(lit.edges.map(String));
      const nodes = new Set(lit.nodes.map(String));
      this.cy.batch(() => {
        this.cy.elements().removeClass("is-hot is-dim is-pinned");
        if (!hot) return;
        this.cy.elements().forEach(ele => {
          const on = ele.isEdge() ? edges.has(ele.id()) : nodes.has(ele.id());
          if (!on) ele.addClass("is-dim");
          // an edge lights as a flow; a node only when it is the hovered state itself
          else if (ele.isEdge() || (hot.kind === "state" && ele.id() === String(hot.id))) ele.addClass("is-hot");
        });
        if (pinnedTarget) pinnedTarget.addClass("is-pinned");
      });
    },
    report(hot, target, originalEvent, pinned) {
      if (!hot || !target) {
        this.$emit("element-hover", null);
        return;
      }
      const oe = originalEvent || {};
      this.$emit("element-hover", {
        group: target.isNode() ? "node" : "edge",
        data: target.data(),
        x: oe.clientX,
        y: oe.clientY,
        hot,
        pinned: !!pinned,
        // rendered boxes, in the diagram container's own pixels
        box: this.boxOf(target),
        // what else is lit — the card keeps clear of it (cytoscape selectors have no :not())
        lit: this.cy.elements()
          .filter(ele => ele.id() !== target.id() && (ele.isEdge() ? ele.hasClass("is-hot") : !ele.hasClass("is-dim")))
          .map(ele => this.boxOf(ele)),
        // every flow the highlight covers, for the card's "Open, In Progress → Cancelled" line
        flows: this.cy.edges(".is-hot").map(edge => ({
          source: edge.data("source"),
          target: edge.data("target"),
          label: edge.data("label"),
          conditional: !!edge.data("conditional")
        }))
      });
    },
    // an edge is reported by its chip (the label), a node by its body
    boxOf(ele) {
      const box = ele.isEdge()
        ? ele.renderedBoundingBox({ includeNodes: false, includeEdges: false, includeLabels: true, includeOverlays: false })
        : ele.renderedBoundingBox({ includeLabels: false, includeOverlays: false });
      return { left: box.x1, top: box.y1, width: box.w, height: box.h };
    },
    tap(target, originalEvent) {
      if (!this.pinnable) return;
      const isActivity = target !== this.cy && target.isEdge() && target.data("activityId") != null;
      const same = isActivity && this.pinned && String(this.pinned.hot.id) === String(target.data("activityId"));
      const wasPinned = !!this.pinned;
      this.pinned = null;
      if (isActivity && !same) {
        const hot = this.hotFor(target);
        this.pinned = { hot, target };
        this.paint(hot, target);
        this.report(hot, target, originalEvent, true);
      } else if (wasPinned) {
        this.light(null);
      }
    },
    /* Parent-driven highlight: light the activities whose form holds this information. */
    highlightInformation(name) {
      if (this.pinned) return;
      this.paint(name ? { kind: "information", name } : null);
    },
    /* Drop any highlight, pinned or not. */
    clearHighlight() {
      this.pinned = null;
      this.light(null);
    }
  },
  watch: {
    diagramData() {
      this.redrawDiagram();
    }
  },
  mounted() {
    this.redrawDiagram();
  },
  beforeDestroy() {
    if (this.cy) this.cy.destroy();
    this.cy = null;
  },
  created() {
    this.diagramId = `cy-${this._uid}`;
  }
};
</script>

<style lang="scss" scoped>
.fts-flow-diagram {
  position: relative;
  width: 100%;
  height: 100%;
  .flow-diagram {
    height: 100%;
    width: 100%;
  }
  .flow-diagram-fab {
    position: absolute;
    top: 0;
    right: 0;
    border: 1px solid var(--fts-border);
    border-radius: 10em;
    background-color: var(--fts-card-background);
  }
}
</style>
